import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"
import { Search, UserPlus } from "lucide-react"
import { useEffect, useState } from "react"
import { audienceKeys, listAudience, MemberAvatar, memberName, plural } from "@/entities/audience"
import { Button } from "@/shared/ui/button"
import { Checkbox } from "@/shared/ui/checkbox"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Input } from "@/shared/ui/input"
import { Label } from "@/shared/ui/label"
import { ScrollArea } from "@/shared/ui/scroll-area"

/** Сколько людей можно отметить вручную, как MAX_SELECTED_USERS в api. */
const MAX_SELECTED = 1000
const PAGE_SIZE = 50

interface RecipientsPickerProps {
	botId: string
	value: string[]
	onChange: (sessionIds: string[]) => void
}

/** «Конкретным пользователям»: поиск по аудитории и галочки, как ручной выбор в Botmaker. */
export function RecipientsPicker({ botId, value, onChange }: RecipientsPickerProps) {
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState("")
	const [debounced, setDebounced] = useState("")
	const [selected, setSelected] = useState<Set<string>>(new Set(value))

	useEffect(() => {
		const timer = window.setTimeout(() => setDebounced(search.trim()), 300)
		return () => window.clearTimeout(timer)
	}, [search])
	useEffect(() => {
		if (open) setSelected(new Set(value))
	}, [open, value])

	const filter = debounced ? { search: debounced } : {}
	const audience = useInfiniteQuery({
		queryKey: [...audienceKeys.list(botId, filter), "picker"],
		queryFn: ({ pageParam }) => listAudience(botId, filter, { skip: pageParam, take: PAGE_SIZE }),
		initialPageParam: 0,
		getNextPageParam: (last, pages) => {
			const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
			return loaded < last.total ? loaded : undefined
		},
		placeholderData: keepPreviousData,
		enabled: open,
	})
	const members = audience.data?.pages.flatMap((page) => page.items) ?? []
	const total = audience.data?.pages[0]?.total ?? 0

	const toggle = (id: string, checked: boolean) =>
		setSelected((current) => {
			const next = new Set(current)
			if (checked && next.size < MAX_SELECTED) next.add(id)
			else next.delete(id)
			return next
		})

	return (
		<>
			<Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
				<UserPlus />
				{value.length > 0
					? `Выбрано ${value.length} ${plural(value.length, ["пользователь", "пользователя", "пользователей"])}`
					: "Выбрать пользователей"}
			</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Конкретные пользователи</DialogTitle>
						<DialogDescription>
							Рассылка уйдёт только отмеченным: до {MAX_SELECTED} человек. Остальные фильтры не
							учитываются.
						</DialogDescription>
					</DialogHeader>
					<div className="relative">
						<Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							autoFocus
							aria-label="Поиск пользователя"
							placeholder="Имя, @username или ID"
							className="h-10 pl-9"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
						/>
					</div>
					<ScrollArea className="h-72 rounded-lg border border-border/60">
						{members.length === 0 ? (
							<p className="p-6 text-center text-sm text-muted-foreground">
								{audience.isLoading ? "Загружаем…" : "Никого не нашлось"}
							</p>
						) : (
							<ul className="divide-y divide-border/60">
								{members.map((member) => (
									<li key={member.id}>
										<Label className="flex cursor-pointer items-center gap-3 px-3 py-2 font-normal hover:bg-accent/50">
											<Checkbox
												checked={selected.has(member.id)}
												onCheckedChange={(checked) => toggle(member.id, checked === true)}
											/>
											<MemberAvatar member={member} />
											<span className="min-w-0 flex-1">
												<span className="block truncate text-sm">{memberName(member)}</span>
												{member.username ? (
													<span className="block truncate text-xs text-muted-foreground">
														@{member.username}
													</span>
												) : null}
											</span>
										</Label>
									</li>
								))}
								{audience.hasNextPage ? (
									<li className="p-2">
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="w-full"
											disabled={audience.isFetchingNextPage}
											onClick={() => void audience.fetchNextPage()}
										>
											{audience.isFetchingNextPage
												? "Загружаем…"
												: `Показать ещё, всего ${total}`}
										</Button>
									</li>
								) : null}
							</ul>
						)}
					</ScrollArea>
					<DialogFooter className="items-center gap-2 sm:justify-between">
						<span className="text-sm text-muted-foreground tabular-nums">
							Отмечено: {selected.size}
						</span>
						<div className="flex gap-2">
							{selected.size > 0 ? (
								<Button type="button" variant="ghost" onClick={() => setSelected(new Set())}>
									Снять все
								</Button>
							) : null}
							<Button
								type="button"
								onClick={() => {
									onChange([...selected])
									setOpen(false)
								}}
							>
								Готово
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
